<template>
  <div id="project-view">
    <hole-side-view
      :isSidebarActive="holeSidebarActive"
      @closeSidebar="toggleHoleDataSidebar"
      :data="holeSidebarData"
      :zones="projectZones"
      @onHoleUpdated="holeUpdated"
      @onHoleInserted="holeInserted"
    />
    <div class="vx-col w-full mb-base">
      <ValidationObserver v-slot="{ handleSubmit }">
        <vx-card ref="infoCard" :title="$t('Drilling.Projects.Edit')" title-color="black"
          :previous-card-action="activeTabIndex > 0"
          save-card-action
          remove-card-action
          @remove="returnToList()"
          @next="next()"
          @previous="previous()"
          @save="handleSubmit(submitData)" >

          <form-wizard
              ref="wizard"
              color="rgba(var(--vs-primary), 1)"
              errorColor="rgba(var(--vs-danger), 1)"
              :title="null"
              :subtitle="null"
              :nextButtonText="$t('Next')"
              :backButtonText="$t('Back')"
              :finishButtonText="$t('Submit')"
              @on-complete="handleSubmit(submitData)"
            >
            <tab-content :title="$t('Drilling.Projects.Tab1')"
                icon="feather icon-settings"
                class="mb-2"
                :before-change="validateStep1">
              <ValidationObserver ref="step1Observer" >
                <div class="p-6">
                  <vs-row class="mb-3">
                    <vs-col vs-w="4" class="pr-4">
                      <ValidationProvider :name="$t('Drilling.Projects.Name')" rules="required" v-slot="{ errors }">
                         <validated-input
                            :label="$t('Drilling.Projects.Name')"
                            v-model="projectName"
                            name="name"
                          ></validated-input>
                        <validation-message>{{ errors[0] }}</validation-message>
                      </ValidationProvider>
                    </vs-col>
                    <vs-col vs-w="4" class="pr-4">
                      <ValidationProvider :name="$t('Drilling.Projects.District')" rules="required" v-slot="{ errors }">
                         <validated-input
                            :label="$t('Drilling.Projects.District')"
                            v-model="projectDistrict"
                            name="district"
                          ></validated-input>
                        <validation-message>{{ errors[0] }}</validation-message>
                      </ValidationProvider>
                    </vs-col>
                    <vs-col vs-w="4" >
                      <ValidationProvider :name="$t('Drilling.Projects.Country')" rules="required" v-slot="{ errors }">
                         <validated-input
                            :label="$t('Drilling.Projects.Country')"
                            v-model="projectCountry"
                            name="country"
                          ></validated-input>
                        <validation-message>{{ errors[0] }}</validation-message>
                      </ValidationProvider>
                    </vs-col>
                  </vs-row>
                  <vs-row class="mb-3">
                    <vs-col vs-w="4" class="pr-4">
                      <label class="vs-input--label">{{ $t('Drilling.Projects.Code') }}</label>
                      <div class="flex items-center">
                        <vs-input
                          class="w-full"
                          :placeholder="$t('Drilling.Projects.Code')"
                          v-model="newCode"
                          @keyup.enter.native="addCode"
                        />
                        <feather-icon
                          icon="PlusCircleIcon"
                          svgClasses="w-6 h-6 hover:text-primary stroke-current"
                          class="ml-2 cursor-pointer"
                          @click="addCode"
                        />
                      </div>
                      <ul class="mt-3 codes-list">
                        <li
                          v-for="(code, index) in projectCodes"
                          :key="index"
                          class="flex items-center justify-between border rounded px-3 py-2 mb-2"
                        >
                          <template v-if="editingCodeIndex === index">
                            <vs-input
                              class="w-full mr-2"
                              v-model="editingCodeValue"
                              @keyup.enter.native="confirmEditCode(index)"
                              @keyup.esc.native="cancelEditCode"
                            />
                            <span class="whitespace-no-wrap flex items-center">
                              <feather-icon
                                icon="CheckIcon"
                                svgClasses="w-5 h-5 hover:text-primary stroke-current cursor-pointer"
                                @click.stop="confirmEditCode(index)"
                              />
                              <feather-icon
                                icon="XIcon"
                                svgClasses="w-5 h-5 hover:text-danger stroke-current cursor-pointer"
                                class="ml-2"
                                @click.stop="cancelEditCode"
                              />
                            </span>
                          </template>
                          <template v-else>
                            <span class="font-medium truncate">{{ code }}</span>
                            <span class="whitespace-no-wrap flex items-center">
                              <feather-icon
                                icon="EditIcon"
                                svgClasses="w-5 h-5 hover:text-primary stroke-current cursor-pointer"
                                @click.stop="startEditCode(index)"
                              />
                              <feather-icon
                                icon="TrashIcon"
                                svgClasses="w-5 h-5 hover:text-danger stroke-current cursor-pointer"
                                class="ml-2"
                                @click.stop="removeCode(index)"
                              />
                            </span>
                          </template>
                        </li>
                      </ul>
                      <validation-message>{{ codesError }}</validation-message>
                    </vs-col>
                    <vs-col vs-w="4" class="pr-4">
                      <ValidationProvider :name="$t('Drilling.Projects.HolePrefix')" rules="length:4" v-slot="{ errors }">
                         <validated-input
                            :label="$t('Drilling.Projects.HolePrefix')"
                            v-model="projectHolePrefix"
                            name="holePrefix"
                          ></validated-input>
                        <validation-message>{{ errors[0] }}</validation-message>
                      </ValidationProvider>
                    </vs-col>
                  </vs-row>
                </div>
              </ValidationObserver>
            </tab-content >
            <tab-content :title="$t('Drilling.Projects.Tab2')"  icon="feather icon-crosshair" class="mb-2" :before-change="validateStep2">
              <zone-list
               :zones="projectZones"
                @onZoneInserted="zoneInserted"
                @onZoneUpdated="zoneUpdated"
                @onDeleteZone="deleteZone">
              </zone-list>
            </tab-content>
            <tab-content :title="$t('Drilling.Projects.Tab3')"  icon="feather icon-thermometer" class="mb-2" :before-change="validateStep3">
              <hole-list
               :holes="projectHoles"
               :zones="projectZones"
                @onAddHole="addHole"
                @onEditHole="editHole"
                @onDeleteHole="deleteHole">
              </hole-list>
            </tab-content>
          </form-wizard>
        </vx-card>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>

import router from '../../../router'
import moduleDrilling from "./../store/moduleDrilling.js";
import { errorHandler } from '@/app/shared/services/error-handler.js';
import holeList from "./components/holeList.vue"
import zoneList from "./components/zoneList.vue"
import holeSideView from "./components/holeSideView.vue"

import { FormWizard, TabContent } from "vue-form-wizard";
import {
  ValidatedInput,
  ValidationMessage
} from "../../shared/components";

export default {
  props: {
    data: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    ValidatedInput,
    ValidationMessage,
    FormWizard,
    TabContent,
    holeList,
    zoneList,
    holeSideView
  },
  data() {
    return {
      selected: [],
      activeTabIndex: 0,
      holeSidebarActive: false,
      holeSidebarData: {},
      contractSidebarActive: false,
      contractSidebarData: {},
      projectId: 0,
      projectMainProjectId: 0,
      projectName: null,
      projectCodes: [],
      originalProjectCodes: [],
      newCode: '',
      editingCodeIndex: null,
      editingCodeValue: '',
      codesError: '',
      projectDistrict : null,
      projectCountry : null,
      projectHolePrefix : null,
      projectBusinessUnitCode : null,
      projectZones : [],
      projectHoles : [],
      selectedTab: 0
    };
  },
  created() {
    if (!moduleDrilling.isRegistered) {
      this.$store.registerModule("drilling", moduleDrilling);
      moduleDrilling.isRegistered = true;
    }

    if (this.$route.params.editData !== undefined && this.$route.params.editData !== null) {
      this.projectId = this.$route.params.editData.projectId;
    } else {
      this.projectId = 0;
    }

    this.projectMainProjectId = Number(this.$store.state.projectId);

    this.$vs.loading();
    Promise.all([
      this.$store.dispatch("drilling/fetchAllCompanies"),
      this.$store.dispatch("drilling/fetchAllProjectCodes")
    ]).then(async () => {
      await this.refreshModel();
    }).catch(error => {
      this.$notifyLoadingError(error.message);
    }).finally(() => {
      this.$vs.loading.close();
    });
  },
  mounted() {
    this.$refs.wizard.activateAll();
  },
  methods: {
    //#region Codes
    addCode() {
      const value = (this.newCode || '').trim();
      if (!value) return;
      if (this.projectCodes.includes(value) || this.isCodeDuplicate(value)) {
        this.codesError = this.$t('Drilling.Projects.CodeUnique');
        return;
      }
      this.projectCodes.push(value);
      this.newCode = '';
      this.codesError = '';
    },
    isCodeDuplicate(value) {
      return this.$store.getters['drilling/isProjectCodeDuplicate'](value, this.originalProjectCodes);
    },
    removeCode(index) {
      this.projectCodes.splice(index, 1);
      if (this.editingCodeIndex === index) this.cancelEditCode();
    },
    startEditCode(index) {
      this.editingCodeIndex = index;
      this.editingCodeValue = this.projectCodes[index];
    },
    confirmEditCode(index) {
      const value = (this.editingCodeValue || '').trim();
      if (!value) return;
      const unchanged = this.projectCodes[index] === value;
      const localDuplicate = this.projectCodes.some((c, i) => c === value && i !== index);
      if (localDuplicate || (!unchanged && this.isCodeDuplicate(value))) {
        this.cancelEditCode();
        return;
      }
      this.projectCodes.splice(index, 1, value);
      this.cancelEditCode();
    },
    cancelEditCode() {
      this.editingCodeIndex = null;
      this.editingCodeValue = '';
    },

    //#endregion

    //#region Holes
    addHole() {
      this.holeSidebarData = {};
      this.toggleHoleDataSidebar(true);
    },
    editHole(data) {
      this.holeSidebarData = data;
      this.toggleHoleDataSidebar(true);
    },
    toggleHoleDataSidebar(val = false) {
      this.holeSidebarActive = val;
    },
    holeInserted(hole) {
      this.projectHoles.unshift(hole);
      this.toggleHoleDataSidebar(false);
    },
    holeUpdated(hole) {
      const holeIndex = this.projectHoles.findIndex((h) => h.holeId === hole.holeId)
      this.projectHoles.splice(holeIndex, 1, hole)
      this.toggleHoleDataSidebar(false);
    },
    deleteHole(holeId, code) {
      const holeIndex = this.projectHoles.findIndex((h) => h.holeId === holeId && h.code === code)
      this.projectHoles.splice(holeIndex, 1)
    },

    //#endregion

    //#region Zones

    zoneInserted(newZone) {
      this.projectZones.unshift(newZone);
    },
    zoneUpdated(zone, oldName) {
      const zoneIndex = this.projectZones.findIndex((z) => z.zoneId === zone.zoneId && z.name === oldName)
      this.projectZones.splice(zoneIndex, 1, zone)
    },
    deleteZone(zoneId, name) {
      const zoneIndex = this.projectZones.findIndex((z) => z.zoneId === zoneId && z.name === name)
      this.projectZones.splice(zoneIndex, 1)
    },

    //#endregion

    next() {
      this.$refs.wizard.nextTab();
      this.activeTabIndex++;
    },
    previous() {
      this.$refs.wizard.prevTab();
      this.activeTabIndex--;
    },

    validateStep1() {
      return new Promise((resolve, reject) => {
        this.$refs.step1Observer.validate().then(result => {
          const codesValid = this.projectCodes.length > 0;
          this.codesError = codesValid ? '' : this.$t('validation.required', { _field_: this.$t('Drilling.Projects.Code') });
          if (result && codesValid) {
            resolve(true);
          } else {
            reject("correct all values");
          }
        });
      });
    },
    validateStep2() {
      return new Promise((resolve) => {
        resolve(true);
      });
    },

    validateStep3() {
      return new Promise((resolve) => {
        resolve(true);
      });
    },

    validateStep4() {
      return new Promise((resolve) => {
        resolve(true);
      });
    },

    async refreshModel() {
      if (this.projectId !== 0) {
        await this.getProject();
      } else {
        this.createProject();
      }
    },
    createProject() {
      this.projectId = 0;
      this.projectName = null;
      this.projectCodes = [];
      this.originalProjectCodes = [];
      this.projectHoles = [];
      this.projectZones = [];
    },
    getProject() {
      return new Promise((resolve, reject) => {
        this.$cqrsGet("Drilling/Projects/Find", { params: { id: this.projectId }}).then((response) => {
          const model = response.data.model;
          this.projectId = model.projectId;
          this.projectMainProjectId = model.mainProjectId;
          this.projectName = model.name;
          this.projectCodes = model.codes || [];
          this.originalProjectCodes = [...(model.codes || [])];
          this.projectDistrict = model.district;
          this.projectCountry = model.country;
          this.projectHolePrefix = model.holePrefix;
          this.projectBusinessUnitCode = model.businessUnitCode;
          this.projectZones = model.zones;
          this.projectHoles = model.holes;
          resolve(response);
        }).catch((error) => {
          this.$notifyLoadingError(error.message);
          reject(error)
        })
      });
    },
    returnToList() {
      router.push("/drilling/projects");
    },

    async submitData() {

      const obj = {
        projectId : this.projectId,
        mainProjectId : this.projectMainProjectId,
        name : this.projectName,
        codes : this.projectCodes,
        district : this.projectDistrict,
        country : this.projectCountry,
        holePrefix : this.projectHolePrefix,
        businessUnitCode : this.projectBusinessUnitCode,
        zones : this.projectZones,
        holes : this.projectHoles,
        contracts : this.projectContracts
      };

      this.$vs.loading();
      const url = obj.projectId > 0 ? 'Drilling/Projects/Edit' : 'Drilling/Projects/Create';
      try {
        const result = await this.$cqrsPost(url, { Project: obj });
        this.$notifySaveSuccess();
        this.projectId = result.data.model.projectId;
        await this.refreshModel();
      } catch (error) {
        errorHandler.savingError(this, error);
      } finally {
        this.$vs.loading.close();
      }
    }
  }
};
</script>
<style lang="scss">
  .list-scrollable {
    height:500px;/* or any height you want */
    overflow-y:auto;
  }
  .codes-list {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
    max-height: 12rem;
    overflow-y: auto;
  }
</style>
